import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, Flex, Heading, Image, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import SignInForm from '@/components/Auth/SignInForm';
import VerifyCodeForm from '@/components/Auth/VerifyCodeForm';
import NewPasswordForm from '@/components/Auth/NewPasswordForm';
import ForgotPasswordForm from '@/components/Auth/ForgotPasswordForm';
import ResetPasswordForm from '@/components/Auth/ResetPasswordForm';
import { AuthStatus, useAuthDispatch, useAuthState } from '@/contexts/AuthContext';
import Page from '@/components/Page';
import Loading from '@/components/Loading';
import { Footer } from '@/components/chakraUIPro/Footer';
import { Navbar } from '@/components/chakraUIPro/Navbar';
import { Alert } from '@/components/ui/alert';
import Link from '@/components/ui/link';

enum FormStates {
  NEW_PASSWORD,

  CONFIRM_EMAIL
}

const SignIn: FC = () => {
  const router = useRouter();
  const { isAuthenticated, isLoading, sessionStatus, error } = useAuthState();
  const { dispatch } = useAuthDispatch();
  const { t } = useTranslation('auth');
  const [tmpUser, setTmpUser]: [any, any] = useState({});
  const [formState, setFormState] = useState<FormStates>();

  // Redirect to account page on sign-in
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Redirect after
    if (isAuthenticated) {
      router.push('/dashboard/');
      return;
    }

    // Set default state
    dispatch({ type: 'clearSession' });
  }, [isAuthenticated, isLoading]);

  const clearSession = () => {
    dispatch({ type: 'clearSession' });
  };

  async function onLogin(nextStep: string, user: any) {
    setTmpUser(user);

    if (nextStep) {
      if (nextStep === 'NEW_PASSWORD_REQUIRED') {
        setFormState(FormStates.NEW_PASSWORD);
        return;
      }

      if (nextStep === 'UserNotConfirmedException') {
        setFormState(FormStates.CONFIRM_EMAIL);
      }
    }
  }

  const getAuthStep = useCallback(() => {
    let step: React.ReactNode;
    switch (sessionStatus) {
      case AuthStatus.ANONYMOUS:
      case AuthStatus.SIGN_IN_ERROR:
      case AuthStatus.PENDING_SIGN_IN:
        step = <SignInForm callback={onLogin} />;
        break;
      case AuthStatus.SIGN_UP_CONFIRM:
      case AuthStatus.PENDING_SIGN_UP_CONFIRM:
      case AuthStatus.SIGN_UP_CONFIRM_ERROR:
        step = <VerifyCodeForm type='CONFIRM_SIGN_UP' user={tmpUser} callback={clearSession} />;
        break;

      case AuthStatus.SIGN_IN_TOTP:
      case AuthStatus.PENDING_SIGN_IN_TOTP:
      case AuthStatus.SIGN_IN_TOTP_ERROR:
        step = <VerifyCodeForm type='CONFIRM_SIGN_IN_WITH_TOTP_CODE' user={tmpUser} callback={clearSession} />;
        break;

      // Reset password
      case AuthStatus.RESET_PASSWORD:
      case AuthStatus.PENDING_RESET_PASSWORD:
      case AuthStatus.RESET_PASSWORD_ERROR:
        step = <ForgotPasswordForm callback={setTmpUser} />;
        break;

      // Reset password confirm
      case AuthStatus.RESET_PASSWORD_CONFIRM:
      case AuthStatus.PENDING_RESET_PASSWORD_CONFIRM:
      case AuthStatus.RESET_PASSWORD_CONFIRM_ERROR:
        step = <ResetPasswordForm email={tmpUser?.email} />;
        break;

      default:
        step = <Loading h='auto' />;
    }

    return step;
  }, [sessionStatus, tmpUser]);

  return (
    <Page
      seoOptions={{
        title: 'Sign in | eezi',
        description: '',
        canonical: 'https://eezi.io/sign-in/'
      }}
      showNav={false}
      maxW='100%'
      p={0}
      m={0}
    >
      <>
        <Navbar />

        <Flex direction={{ base: 'column', md: 'row' }} overflow='hidden' justifyContent='center'>
          <Flex flexDir='row'>
            <Flex
              display={{ base: 'none', md: 'block' }}
              flexDir='column'
              maxW='50%'
              py='16'
              px='20'
              alignItems='self-start'
            >
              <Image src='/images/sign-in.png' alt='Sign in to eezi' p={{ base: 4, md: 10 }} />
            </Flex>
            <Box overflowY='auto' flex='1' py={{ base: '10', md: '16' }} px={{ base: '6', md: '10' }}>
              <VStack gap={6} maxW='sm' mx='auto'>
                <Box textAlign='center' mb={{ base: 4, md: 10 }}>
                  <Heading as='h1' size='3xl' fontWeight='extrabold' letterSpacing='tight'>
                    {t('signIn.title')}
                  </Heading>
                  <Text mt='3'>
                    {t('signIn.noAccount')} <Link href='/sign-up'>{t('signIn.signUpLink')}</Link>
                  </Text>
                </Box>
                <>
                  {/* Errors */}
                  {error && <Alert status='error' title={error?.message} />}
                  {/* Auth step */}
                  {getAuthStep()}
                  {/* Additional steps */}
                </>
                <>
                  {formState === FormStates.CONFIRM_EMAIL && <VerifyCodeForm user={tmpUser} callback={clearSession} />}
                  {formState === FormStates.NEW_PASSWORD && <NewPasswordForm callback={clearSession} />}
                </>
              </VStack>
            </Box>
          </Flex>
        </Flex>
        {/* Footer */}
        <Box>
          <Box maxW='8xl' mx='auto' p={8} textAlign='left'>
            <Footer />
          </Box>
        </Box>
      </>
    </Page>
  );
};
export default SignIn;
